export type WorkOrderStatus = "PLANNED" | "IN_PROGRESS" | "COMPLETED" | "CLOSED" | "CANCELLED";
export type StageStatus = "PENDING" | "IN_PROGRESS" | "DONE" | "SKIPPED";

export const WO_STATUS_LABELS: Record<string, string> = {
  PLANNED: "Planned",
  IN_PROGRESS: "In progress",
  COMPLETED: "Completed",
  CLOSED: "Closed",
  CANCELLED: "Cancelled",
};

export const STAGE_STATUS_LABELS: Record<string, string> = {
  PENDING: "Pending",
  IN_PROGRESS: "In progress",
  DONE: "Done",
  SKIPPED: "Skipped",
};

export const PRIORITIES: Record<string, string> = { LOW: "Low", NORMAL: "Normal", HIGH: "High", URGENT: "Urgent" };

export interface ProcessRecord {
  id: string;
  code: string;
  name: string;
  sort_order: number;
  default_days: number;
  description: string | null;
  status: boolean;
}

export interface RouteStep {
  process_id: string;
  process_code: string;
  process_name: string;
  days: number;
}

export interface WorkOrderStageRecord {
  id: string;
  sort_order: number;
  process_code: string;
  process_name: string;
  execution: "IN_HOUSE" | "JOB_WORK";
  supplier_id: string | null;
  supplier?: { id: string; name: string } | null;
  planned_start: string | null;
  planned_end: string | null;
  actual_start: string | null;
  actual_end: string | null;
  input_qty: number | string | null;
  output_qty: number | string | null;
  wastage_qty: number | string | null;
  stage_status: StageStatus;
  remarks: string | null;
  overdue?: boolean;
}

export interface WorkOrderHistoryRecord {
  id: string;
  created_on: string;
  action: string;
  from_status: string | null;
  to_status: string | null;
  comment: string | null;
  by: string | null;
}

export interface WorkOrderRecord {
  id: string;
  wo_number: string;
  product_id: string;
  variant_id: string | null;
  product_name: string;
  variant_name: string | null;
  planned_qty: number | string;
  unit: string;
  produced_qty: number | string | null;
  order_id: string | null;
  order_line_id: string | null;
  due_date: string | null;
  priority: string;
  work_order_status: WorkOrderStatus;
  owner_id: string | null;
  notes: string | null;
  cancelled_reason: string | null;
  created_on: string;
  started_at: string | null;
  completed_at: string | null;
  order?: { id: string; order_number: string } | null;
  account?: { id: string; name: string } | null;
  owner?: { id: string; first_name: string; last_name?: string | null } | null;
  /** From the list */
  stages_total?: number;
  stages_done?: number;
  current_stage_name?: string | null;
  current_stage_status?: StageStatus | null;
  late?: boolean;
  /** From the detail */
  stages?: WorkOrderStageRecord[];
  history?: WorkOrderHistoryRecord[];
  planned_finish?: string | null;
  wastage_total?: number;
  yield_pct?: number | null;
}

export interface BoardCard {
  id: string;
  wo_number: string;
  product_name: string;
  variant_name: string | null;
  planned_qty: number;
  unit: string;
  due_date: string | null;
  priority: string;
  order_number: string | null;
  account_name: string | null;
  process_name: string;
  stage_status: StageStatus;
  execution: string;
  job_worker: string | null;
  planned_end: string | null;
  late: boolean;
}

export interface BoardColumn {
  process_code: string;
  process_name: string;
  cards: BoardCard[];
}

export interface ProductionSummary {
  planned: number;
  in_progress: number;
  completed: number;
  past_due: number;
  due_this_week: number;
  late_stages: number;
}

export interface OrderProduction {
  work_orders: WorkOrderRecord[];
  lines: Array<{ order_line_id: string; ordered_qty: number; planned_qty: number; remaining_qty: number }>;
}

export const fmtDay = (d?: string | null) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "-");
export const qty = (n: number | string | null | undefined, unit?: string | null) => (n == null || n === "" ? "-" : `${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 3 })}${unit ? ` ${unit}` : ""}`);

export interface MaterialRow {
  id: string;
  stage_id: string | null;
  stage_name: string | null;
  challan_id: string | null;
  challan_number: string | null;
  product_name: string;
  variant_name: string | null;
  lot_number: string;
  unit_no: string;
  quantity: number;
  unit: string;
  returned_qty: number;
  material_status: "ISSUED" | "SETTLED";
  created_on: string;
}

export interface MaterialData {
  rows: MaterialRow[];
  totals: Array<{ unit: string; issued: number; returned: number; used: number; open: number }>;
  open_count: number;
}

export type ChallanStatus = "SENT" | "PARTIAL" | "RECEIVED" | "CANCELLED";

export const CHALLAN_STATUS_LABELS: Record<string, string> = { SENT: "Sent", PARTIAL: "Part received", RECEIVED: "Received", CANCELLED: "Cancelled" };

export interface ChallanRecord {
  id: string;
  challan_number: string;
  work_order_id: string;
  stage_id: string;
  supplier_id: string;
  challan_date: string;
  expected_return: string | null;
  vehicle_no: string | null;
  challan_status: ChallanStatus;
  sent_qty: number | string;
  unit: string;
  roll_count: number;
  received_qty: number | string;
  wastage_qty: number | string;
  notes: string | null;
  cancelled_reason: string | null;
  overdue?: boolean;
  supplier?: { id: string; name: string } | null;
  work_order?: { id: string; wo_number: string; product_name: string } | null;
}

export interface ChallanDetail extends ChallanRecord {
  stage_name: string | null;
  rolls: MaterialRow[];
  receipts: Array<{ id: string; received_date: string; received_qty: number | string; unit: string; wastage_qty: number | string; reference_no: string | null; is_final: boolean; notes: string | null }>;
  receipt_unit: string;
  shortfall: number | null;
}

export interface OutputRow {
  id: string;
  unit_id: string;
  unit_no: string;
  lot_number: string;
  quantity: number;
  unit: string;
  grade: string;
  reserved: boolean;
  unit_status: string | null;
  quantity_now: number | null;
  created_on: string;
}

export interface OutputData {
  rows: OutputRow[];
  total_qty: number;
  unit: string | null;
  produced_qty: number | null;
  planned_qty: number | null;
}

export interface ProductionReport {
  by_process: Array<{ process_code: string; process_name: string; stages: number; input_qty: number; output_qty: number; wastage_qty: number; wastage_pct: number | null; late_stages: number }>;
  by_job_worker: Array<{ supplier_id: string; supplier_name: string; unit: string; challans: number; sent_qty: number; received_qty: number; wastage_qty: number; open_challans: number; overdue_challans: number }>;
  by_work_order: Array<{ id: string; wo_number: string; product_name: string; variant_name: string | null; unit: string; work_order_status: string; planned_qty: number; produced_qty: number | null; first_input: number | null; wastage_qty: number; in_stock_qty: number; yield_pct: number | null }>;
  materials: Array<{ product_id: string; product_name: string; unit: string; issued_qty: number; returned_qty: number; used_qty: number; open_qty: number }>;
}

export interface ProductionPerformance {
  completed: number;
  with_due: number;
  on_time: number;
  on_time_pct: number | null;
  avg_lead_days: number | null;
  avg_days_late: number | null;
  by_process: Array<{ process_code: string; process_name: string; stages: number; planned_days: number | null; actual_days: number | null; late_stages: number }>;
}
